import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { PartialPick } from '@@types/CommonTypes';
import { ArrElement } from '@@types/CommonTypes';

import { GetTenantByNameQuery } from '@graphql/generated/graphql';

type CompanyInfoType = ArrElement<GetTenantByNameQuery['tenant']>;

export interface GlobalStoreState {
    blockScroll?: boolean;
    companyInfo?: CompanyInfoType;
}

interface GlobalStore extends GlobalStoreState {
    setBlockScroll: (value: boolean) => void;
    setCompanyInfo: (value: CompanyInfoType) => void;
}

export const GlobalStoreDefaultState: GlobalStoreState = {
    blockScroll: false,
    companyInfo: undefined,
};

/**
 * For performance optimization, it is recommended to use `useGlobalStoreShallow` instead,
 * which returns a shallow copy of the state and prevents unnecessary re-renders.
 */
export const useGlobalStore = create<GlobalStore>()(
    devtools(
        (set) => ({
            ...GlobalStoreDefaultState,
            setBlockScroll: (blockScroll) => {
                set(() => ({
                    blockScroll,
                }));
            },
            setCompanyInfo: (companyInfo) => {
                set(() => ({ companyInfo }));
            },
        }),
        {
            name: 'GlobalStore',
        },
    ),
);

/**
 * A hook that returns a shallow copy of the GlobalStore state.
 * Shallow copies are used to optimize performance by preventing unnecessary re-renders in Zustand.
 *
 * @param keys - An array of keys to be included in the returned state.
 * @returns - A shallow copy of the GlobalStore state that only includes the specified keys.
 */
export const useGlobalStoreShallow = <K extends keyof GlobalStore>(
    keys: K[],
): PartialPick<GlobalStore, K> =>
    useGlobalStore(
        useShallow((state) =>
            keys.reduce(
                (prevObject, currentKey) => ({
                    ...prevObject,
                    [currentKey]: state[currentKey],
                }),
                {} as PartialPick<GlobalStore, K>,
            ),
        ),
    );

/**
 * A utility function to get the current state of the GlobalStore.
 * This function is particularly useful when you need to access the state outside of a React component.
 *
 * @returns - The current state of the GlobalStore.
 */
export const getGlobalStore = useGlobalStore.getState;

export const resetGlobalStore = useGlobalStore.setState(
    GlobalStoreDefaultState,
);
